import type { SongEntry } from './SongEntry.js';
import type { AssetName, SongRepository } from './SongRepository.js';

/**
 * `SongRepository` on IndexedDB.
 *
 * ## Schema
 *
 * ```
 * database  mandalizm-library   version 1
 *
 * songs     keyPath "packId"            one SongEntry per song
 * assets    keyPath ["packId","name"]   { packId, name, blob }
 *           index   "byPack" -> packId
 * ```
 *
 * Two stores rather than one because they are read at completely different
 * times. `songs` is read whole, once, at launch, to draw Song Select — it is a
 * few hundred bytes per song and holds no payload. `assets` is read one song at
 * a time, on PLAY, and holds everything large.
 *
 * ## Blobs, not ArrayBuffers
 *
 * Every payload is stored as a `Blob`. This is the single decision that makes a
 * 200 MB music video workable: browsers back a stored `Blob` with a file on
 * disk, so reading one out does not pull 200 MB into the JavaScript heap, and
 * `URL.createObjectURL(blob)` hands `<video>` a handle to that file rather than
 * a copy. An `ArrayBuffer` would be materialised in memory on every read.
 *
 * ## Atomicity
 *
 * `put` writes the index record and every asset in **one** transaction, and
 * clears assets the new version does not have. A failure aborts the whole thing,
 * so there is no state in which Song Select lists a song whose music is missing.
 * Nothing is awaited between opening the transaction and issuing its requests —
 * an IndexedDB transaction commits the moment the task queue drains, so an
 * `await` in the middle would close it early.
 */

export const DATABASE_NAME = 'mandalizm-library';
export const DATABASE_VERSION = 1;
export const SONG_STORE = 'songs';
export const ASSET_STORE = 'assets';
const PACK_INDEX = 'byPack';

interface AssetRecord {
  readonly packId: string;
  readonly name: AssetName;
  readonly blob: Blob;
}

export class IndexedDbSongRepository implements SongRepository {
  private readonly factory: IDBFactory | undefined;
  private readonly databaseName: string;
  private open: Promise<IDBDatabase> | null = null;
  private handle: IDBDatabase | null = null;

  constructor(factory?: IDBFactory, databaseName: string = DATABASE_NAME) {
    this.factory = factory;
    this.databaseName = databaseName;
  }

  /** True when this browser has IndexedDB at all. Private mode may not. */
  static get available(): boolean {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  }

  async list(): Promise<SongEntry[]> {
    const database = await this.database();
    return request<SongEntry[]>(
      database.transaction(SONG_STORE, 'readonly').objectStore(SONG_STORE).getAll(),
    );
  }

  async get(packId: string): Promise<SongEntry | null> {
    const database = await this.database();
    const value = await request<SongEntry | undefined>(
      database.transaction(SONG_STORE, 'readonly').objectStore(SONG_STORE).get(packId),
    );
    return value ?? null;
  }

  async put(entry: SongEntry, assets: ReadonlyMap<AssetName, Blob>): Promise<void> {
    const database = await this.database();
    const transaction = database.transaction([SONG_STORE, ASSET_STORE], 'readwrite');
    const songs = transaction.objectStore(SONG_STORE);
    const store = transaction.objectStore(ASSET_STORE);

    // Clear first, then write. Replacing a song that had a video with one that
    // does not must not leave the old video occupying storage and, worse, being
    // found by a later read.
    const keys = store.index(PACK_INDEX).getAllKeys(IDBKeyRange.only(entry.packId));
    keys.onsuccess = () => {
      for (const key of keys.result) store.delete(key);
      for (const [name, blob] of assets) {
        const record: AssetRecord = { packId: entry.packId, name, blob };
        store.put(record);
      }
      songs.put(entry);
    };
    return finished(transaction);
  }

  async asset(packId: string, name: AssetName): Promise<Blob | null> {
    const found = await this.assets(packId, [name]);
    return found.get(name) ?? null;
  }

  async assets(packId: string, names: readonly AssetName[]): Promise<Map<AssetName, Blob>> {
    const database = await this.database();
    const transaction = database.transaction(ASSET_STORE, 'readonly');
    const store = transaction.objectStore(ASSET_STORE);
    const result = new Map<AssetName, Blob>();
    for (const name of names) {
      const query = store.get([packId, name]);
      query.onsuccess = () => {
        const record = query.result as AssetRecord | undefined;
        if (record !== undefined) result.set(name, record.blob);
      };
    }
    await finished(transaction);
    return result;
  }

  async remove(packId: string): Promise<boolean> {
    const database = await this.database();
    const transaction = database.transaction([SONG_STORE, ASSET_STORE], 'readwrite');
    const songs = transaction.objectStore(SONG_STORE);
    const store = transaction.objectStore(ASSET_STORE);
    let existed = false;

    const existing = songs.get(packId);
    existing.onsuccess = () => {
      existed = existing.result !== undefined;
      songs.delete(packId);
      const keys = store.index(PACK_INDEX).getAllKeys(IDBKeyRange.only(packId));
      keys.onsuccess = () => {
        for (const key of keys.result) store.delete(key);
      };
    };
    await finished(transaction);
    return existed;
  }

  close(): void {
    this.handle?.close();
    this.handle = null;
    this.open = null;
  }

  private database(): Promise<IDBDatabase> {
    if (this.open === null) this.open = this.openDatabase();
    return this.open;
  }

  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      // Resolve lazily: an embedded host without storage can still play Demo.
      const factory = this.factory ?? globalThis.indexedDB;
      if (factory === undefined) throw new Error('The song library is unavailable in this browser.');
      const opening = factory.open(this.databaseName, DATABASE_VERSION);
      opening.onupgradeneeded = () => {
        const database = opening.result;
        if (!database.objectStoreNames.contains(SONG_STORE)) {
          database.createObjectStore(SONG_STORE, { keyPath: 'packId' });
        }
        if (!database.objectStoreNames.contains(ASSET_STORE)) {
          const store = database.createObjectStore(ASSET_STORE, { keyPath: ['packId', 'name'] });
          store.createIndex(PACK_INDEX, 'packId', { unique: false });
        }
      };
      opening.onsuccess = () => {
        const database = opening.result;
        // Another tab upgrading the schema would otherwise be blocked forever by
        // this connection. Closing loses this tab's library view, which is the
        // lesser problem, and the next call reopens.
        database.onversionchange = () => this.close();
        this.handle = database;
        resolve(database);
      };
      opening.onerror = () => reject(errorOf(opening, 'The song library could not be opened.'));
      opening.onblocked = () =>
        reject(new Error('The song library is open in another tab and could not be upgraded.'));
    });
  }
}

// ---------------------------------------------------------------------------

function request<T>(source: IDBRequest): Promise<T> {
  return new Promise((resolve, reject) => {
    source.onsuccess = () => resolve(source.result as T);
    source.onerror = () => reject(errorOf(source, 'The song library read failed.'));
  });
}

/**
 * Resolves when the transaction commits, rejects when it aborts.
 *
 * `onabort` matters as much as `onerror` here: exceeding the storage quota
 * aborts the transaction rather than failing an individual request, and that is
 * exactly the case a 200 MB video is most likely to hit.
 */
function finished(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(errorOf(transaction, 'The song library write failed.'));
    transaction.onabort = () =>
      reject(
        errorOf(
          transaction,
          'The song library write was aborted, most likely because storage is full.',
        ),
      );
  });
}

function errorOf(source: { error: DOMException | null }, fallback: string): Error {
  const error = source.error;
  if (error === null) return new Error(fallback);
  return new Error(`${error.name}: ${error.message || fallback}`);
}
